import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Uploader from './Uploader';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { useMppContext } from '../context/MppContextEnhanced';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

interface MppUploaderProps {
  refreshParentCallback?: () => void;
  onUploadSuccess?: () => void;
}

const MppUploader: React.FC<MppUploaderProps> = ({ refreshParentCallback, onUploadSuccess }) => {
  const { uploadInProgress, notifyUploadStart, notifyUploadComplete } = useMppContext();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Enhanced upload handler that coordinates with context
  const handleUploadSuccess = async () => {
    try {
      // Notify context that upload completed
      await notifyUploadComplete();

      // Call parent callback if provided (for backward compatibility)
      if (refreshParentCallback) {
        refreshParentCallback();
      }

      // Notify parent of successful upload (for snackbar notification)
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      // Close modal after successful upload
      handleClose();
    } catch (error) {
      console.error('Error during upload completion:', error);
    }
  };

  const handleUploadStart = () => {
    notifyUploadStart();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mr: 1 }}>
      <Button
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        sx={{
          background: uploadInProgress ? 'rgb(255, 152, 0)' : 'rgb(31, 77, 131)',
          '&:hover': {
            background: uploadInProgress ? 'rgb(255, 183, 77)' : 'rgb(0, 136, 255)',
          },
          color: (theme) => theme.palette.secondary.contrastText,
          textTransform: 'none',
        }}
        disabled={uploadInProgress}
        onClick={handleOpen}
        data-testid="ims-add-schedule"
      >
        Add Schedule
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Select .mpp File to Upload
            {uploadInProgress && ' (Uploading...)'}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
          </Typography>
          <Uploader
            onUpload={handleUploadSuccess}
            onUploadStart={handleUploadStart}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default MppUploader;
